import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import type { Rarity } from '#src/__generated__/globalTypes.ts';
import { useConfigContext } from '#src/contexts/config/index.ts';
import { getRarityColor } from '#src/lib/colors.ts';
import type { Color } from '#src/style/types.ts';

import type { getSpecialSetCardPackGroupsThatAreNotFeatured_cardPackGroup } from './__generated__/index.graphql.ts';
import { background as baseSetBackground } from './base/index.ts';
import {
  SET_STELLAR,
  background as stellarSetBackground,
} from './stellar/index.ts';
import { SET_WINTER, background as winterSetBackground } from './wntr/index.ts';

/** temporary config for the card factory before adding the proper config */
export const STELLAR_EDITION = [
  'stellar_standard_base',
  'stellar_shiny_base',
  'stellar_shiny_jersey_number',
  'stellar_shiny_meteor_striker',
  'stellar_holo_base',
  'stellar_holo_action_shot',
  'stellar_full_art_base',
  'stellar_full_art_signed',
];
export const SET_EDITION = [
  ...SET_WINTER.cardSet.activeEditionSlugs,
  ...STELLAR_EDITION,
];

export const useCommonGameSet = () => {
  const { cardSet } = useConfigContext();
  const currentSet = cardSet?.currentSet;
  if (currentSet?.slug === 'stellar') {
    return { ...SET_STELLAR, endDate: currentSet.endDate };
  }
  return { ...SET_WINTER, endDate: currentSet?.endDate };
};

export const useCurrentSetBackground = () => {
  const currentSet = useCommonGameSet();
  return currentSet.background;
};

export function useSetBackground() {
  return (name: string | undefined, skipDefaultBackground?: true) => {
    if (name?.match(SET_WINTER.slugPattern)) {
      return winterSetBackground;
    }
    if (name?.match(SET_STELLAR.slugPattern)) {
      return stellarSetBackground;
    }
    return skipDefaultBackground
      ? { background: undefined, backgroundBlendMode: undefined }
      : baseSetBackground;
  };
}

export const useActiveSetEdition = () => {
  const commonGameSet = useCommonGameSet();
  return [
    ...commonGameSet.cardSet.activeEditionSlugs,
  ] as const satisfies string[];
};

export const useSupportedCardSets = () => {
  const commonGameSet = useCommonGameSet();
  return [...commonGameSet.cardSet.supportedSlugs] as const satisfies string[];
};

export const useIsSupportedCardSet = () => {
  const supportedCardSets = useSupportedCardSets();
  return (setSlug: unknown): setSlug is (typeof supportedCardSets)[number] => {
    return !!setSlug && supportedCardSets.some(s => s === setSlug);
  };
};

export const useIsSpecialCardSet = () => {
  const commonGameSet = useCommonGameSet();
  return (setSlug: Nullable<string>) => {
    return !!setSlug && setSlug !== commonGameSet.cardSet.baseSlug;
  };
};

export const useIsCurrentCardSet = () => {
  const commonGameSet = useCommonGameSet();
  return (setSlug: Nullable<string>) => {
    return !!setSlug && setSlug === commonGameSet.cardSet.activeSlug;
  };
};

export const useGetDisplayCardSetName = () => {
  const commonGameSet = useCommonGameSet();
  return (setSlug: Nullable<string>) => {
    if (setSlug === commonGameSet.cardSet.activeSlug) {
      return commonGameSet.cardSet.displayName;
    }
    return setSlug;
  };
};

const getSpecialSetCardPackGroupsThatAreNotFeaturedFragment = gql`
  fragment getSpecialSetCardPackGroupsThatAreNotFeatured_cardPackGroup on CardPackGroup {
    id
    featured
    cardSet {
      slug
    }
  }
` as TypedDocumentNode<getSpecialSetCardPackGroupsThatAreNotFeatured_cardPackGroup>;

export const useGetSpecialSetCardPackGroupsThatAreNotFeatured = () => {
  const isCurrentCardSet = useIsCurrentCardSet();
  return <
    T extends getSpecialSetCardPackGroupsThatAreNotFeatured_cardPackGroup,
  >(
    cardPackGroups: NonNullable<T[]>
  ): T[] => {
    return cardPackGroups.filter(
      ({ featured, cardSet }) => isCurrentCardSet(cardSet?.slug) && !featured
    );
  };
};

useGetSpecialSetCardPackGroupsThatAreNotFeatured.fragments = {
  cardPackGroup: getSpecialSetCardPackGroupsThatAreNotFeaturedFragment,
};

const MIX_SHARD_COLOR: Color = 'var(--c-green-400)';

export const useGetRarityColor = () => {
  const commonGameSet = useCommonGameSet();
  const isSupportedCardSet = useIsSupportedCardSet();
  return (
    rarity: Rarity,
    opts?: { opacity?: number; cardSet?: Nullable<string> }
  ): Color => {
    const { opacity = 1, cardSet } = opts || {};
    if (isSupportedCardSet(cardSet)) {
      return commonGameSet.colors.main;
    }
    return getRarityColor(rarity, { opacity });
  };
};

export const useGetRarityShardColor = () => {
  const getRarityColorWithCardSet = useGetRarityColor();
  return (rarity: Nullable<Rarity>, cardSet: Nullable<string>) => {
    if (rarity) {
      return getRarityColorWithCardSet(rarity, { cardSet });
    }
    return MIX_SHARD_COLOR;
  };
};

export const useSetLogoImage = () => {
  const commonGameSet = useCommonGameSet();
  return (name: string | undefined) => {
    if (name?.match(SET_WINTER.slugPattern)) {
      return SET_WINTER.visual.brand.logoImage;
    }
    return commonGameSet.visual.brand.logoImage;
  };
};
